import { useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';

import {
  ApurementError,
  ApurementReportResponseDto,
} from 'lib_api/lib/api/gen';

import {
  AlertMessage,
  useAlertMessage,
} from 'hooks/alertMessage/useAlertMessage';
import { useApi } from 'hooks/ApiStoreContext';
import { getErrorDtoOrThrow, getStringOrThrow } from 'typeGuards/typeGuards';

const formatErrors = (errors: Array<ApurementError>) => {
  return errors
    .map(error => `Dossier "${error.idDossier}" : ${error.error}`)
    .join('\n');
};

interface UseGetApurementDossierReportRet {
  alertMessage?: AlertMessage;
  resetAlertMessage: () => void;
}

export function useGetApurementDossierReport(
  apurementId: string | null,
): UseGetApurementDossierReportRet {
  const { AdminFonctionnelControllerApi: controller } = useApi();
  const {
    alertMessage,
    setErrorMessage,
    setSuccessMessage,
    resetAlertMessage,
  } = useAlertMessage();

  const getApurementReport = useQuery<ApurementReportResponseDto, Response>({
    queryKey: ['getApurementDossierReport', apurementId],
    enabled: !!apurementId,
    queryFn: () =>
      controller.getApurementReportUsingGET(getStringOrThrow(apurementId)),
    refetchInterval: query => (query.state.data?.dateFin ? false : 10_000),
  });

  useEffect(() => {
    if (getApurementReport.isFetching) {
      resetAlertMessage();
    }
  }, [getApurementReport.isFetching, resetAlertMessage]);

  useEffect(() => {
    if (getApurementReport.isError) {
      getApurementReport.error
        .json()
        .then(jsonError => {
          const errorDto = getErrorDtoOrThrow(jsonError);
          setErrorMessage(errorDto.message);
        })
        .catch(() => {
          throw new Error('Could not parse get apurement error response');
        });
    }
  }, [getApurementReport.isError, setErrorMessage]);

  useEffect(() => {
    if (getApurementReport.isSuccess) {
      const reportResponse = getApurementReport.data;
      if (!reportResponse.dateFin) {
        return;
      }

      if (reportResponse.erreurs && reportResponse.erreurs.length > 0) {
        setErrorMessage(formatErrors(reportResponse.erreurs));
      } else {
        setSuccessMessage('Apurement des dossiers terminé avec succès.');
      }
    }
  }, [
    getApurementReport.isSuccess,
    getApurementReport.data,
    setSuccessMessage,
    setErrorMessage,
  ]);

  return {
    alertMessage,
    resetAlertMessage,
  };
}
